import {
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  NgZone,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { RouterComponents } from '@app/orgs/constants';
import { DownloadableReportType } from '@app/reporting/downloadable/models/downloadable-reports.model';
import { DownloadableReportsApiService } from '@app/reporting/downloadable/services/downloadable-reports-api.service';
import { DownloadableReportsModalsService } from '@app/reporting/downloadable/services/downloadable-reports-modals.service';
import {
  DownloadableReport,
  ReportingGroup,
} from '@app/reporting/models/reporting-api.model';
import { ReportingFlagsService } from '@app/reporting/services/reporting-flags.service';
import {
  DataColumn,
  DataColumnDate,
} from '@app/shared/components/data-table/data-table';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { AuthService } from '@app/shared/services/auth.service';
import { DateRangeService } from '@app/shared/services/date-range.service';
import { NotifierService } from '@app/shared/services/notifier.service';
import { TrackerService } from '@app/shared/services/tracker.service';
import { orderBy } from '@app/shared/utils/common-utils';
import { Loadable, loadWith } from '@dg/shared-rxjs';
import { WindowToken } from '@app/shared/window.token';
import {
  DfIconArrowDownTray16,
  DfIconPlus16,
  DfIconRegistry,
} from '@lib/fresco';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, Observable } from 'rxjs';
import {
  catchError,
  finalize,
  map,
  repeat,
  switchMap,
  takeWhile,
  tap,
} from 'rxjs/operators';

const POLL_INTERVAL = 15000;
const REPORTING_API = '/api/reporting/downloadable';

@Component({
  selector: 'dgx-downloadable-reports',
  templateUrl: './downloadable-reports.component.html',
})
export class DownloadableReportsComponent
  extends SubscriberBaseDirective
  implements OnInit, Loadable {
  @Input() public location: string;
  @Input() public source: string;
  @Input() public initDaysPrior = 30;
  @Input() public maxDays = 93;
  @Input() public defaultGroupId?: number;
  @Input() public defaultGroupIdList?: string;
  @Input() public defaultReport?: string;
  // Optionally, the parent can pass a pathwayId in which case we only show
  // or generate the Pathway (id=22) reports
  @Input() public pathwayId?: number;

  @ViewChild('groupNameColumn', { static: true })
  public groupNameColumnTemplate: TemplateRef<any>;
  @ViewChild('dateRangeColumn', { static: true })
  public dateRangeColumnTemplate: TemplateRef<any>;
  @ViewChild('dateCreatedColumn', { static: true })
  public dateCreatedColumnTemplate: TemplateRef<any>;
  @ViewChild('reportStatusColumn', { static: true })
  public reportStatusColumnTemplate: TemplateRef<any>;

  public readonly CLASS_NAME = RouterComponents.DOWNLOADABLE_REPORTS;

  public readonly i18n = this.translate.instant([
    'dgDownloadableReports_DataSet',
    'dgDownloadableReports_DateCreated',
    'dgDownloadableReports_DateRange',
    'dgDownloadableReports_Download',
    'Core_Followers',
    'dgDownloadableReports_DownloadableReports',
    'dgDownloadableReports_Generate',
    'dgDownloadableReports_GenerateReport',
    'dgDownloadableReports_Generating',
    'dgDownloadableReports_ReportType',
    'Core_Group',
  ]);

  // auth user
  public authUser = this.authService.authUser;
  public isSkillInventoryClient = this.authUser.isSkillInventoryClient;
  public isSkillAnalyticsClient = this.authUser.isSkillAnalyticsClient;
  public orgId = this.authUser.defaultOrgInfo.organizationId;

  // reports data
  public availableReports?: DownloadableReportType[];
  public canViewReports?: boolean;
  public groups?: ReportingGroup[];
  public pathwayReport?: DownloadableReportType;
  public reports?: DownloadableReport[];
  public columns: DataColumn[];

  // state
  public isLoading = true;

  constructor(
    private route: ActivatedRoute,
    private authService: AuthService,
    private translate: TranslateService,
    private tracker: TrackerService,
    private notifier: NotifierService,
    private dateRangeService: DateRangeService,
    private reportingFlagsService: ReportingFlagsService,
    private downloadableReportsApiService: DownloadableReportsApiService,
    private downloadableReportsModalsService: DownloadableReportsModalsService,
    private iconRegistry: DfIconRegistry,
    @Inject(WindowToken) private windowRef: Window,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) {
    super();

    this.iconRegistry.registerIcons([DfIconPlus16, DfIconArrowDownTray16]);
  }

  public ngOnInit() {
    this.getRouteSnapshot();
    this.getReports();
    this.initializeColumns();
  }

  public getReports(): void {
    const { canViewContentCompletionsReport } = this.reportingFlagsService;

    // We DO need the report types in the pathway reporting case since we
    // need to find the correct report details to create a new one.
    const load = this.downloadableReportsApiService
      .getDownloadableReports(this.location, this.pathwayId)
      .pipe(
        tap((data) => {
          this.canViewReports = data.canViewOrgReports;
          this.groups = data.groups.sort((a, b) => orderBy(a, b, 'name'));

          // TODO: alternate method to extract the pathway report
          // this.pathwayReport = data.reports.find(r => r.reportType === 'Pathway Details Download')

          this.availableReports = data.reports.filter((report) => {
            if (report.reportType === 'Pathway Details Download') {
              // Save the PathwayReport in case we need it later.
              this.pathwayReport = report;
            } else if (
              this.authUser.disableRatingsInsights &&
              report.reportType === 'Top 1000 Skills Rated'
            ) {
              // hide all Skill Reports if disableRatingsInsights = true
              return false;
            } else if (
              report.reportType === 'Content Completions Download' &&
              !canViewContentCompletionsReport
            ) {
              return false;
            }
            return true;
          });
        }),
        switchMap(() => {
          // In the Pathway reporting case, we need to first get the report types
          // from the server, only then we can request the actual reports.
          if (this.pathwayId) {
            return this.downloadableReportsApiService.getDownloadablePathwayReportsForUser(
              this.pathwayId,
              this.pathwayReport.reportId
            );
          }

          return this.downloadableReportsApiService
            .getDownloadableReportsForUser()
            .pipe(
              map((reports) =>
                reports.filter(
                  (report) => report.reportId !== this.pathwayReport?.reportId
                )
              )
            );
        }),
        tap((reportsForUser) => {
          this.reports = reportsForUser;
        }),
        repeat({ delay: POLL_INTERVAL }),
        takeWhile((downloads) => {
          // If there are in-progress reports, continue polling
          const hasInProgress = downloads.some(
            (d) => d.reportStatus === 'InProgress'
          );
          return hasInProgress;
        }),
        this.takeUntilDestroyed()
      );

    loadWith(load, this)
      .pipe(
        finalize(() => {
          this.cdr.markForCheck();
        })
      )
      .subscribe();
  }

  public trackReportDownload(report: DownloadableReport) {
    const properties: Record<string, any> = {
      location: this.location,
      reportName: report.reportName,
      reportId: report.reportId,
      startDate: report.startDate
        ? this.dateRangeService.getDateFormat(report.startDate)
        : undefined,
      endDate: report.endDate
        ? this.dateRangeService.getDateFormat(report.endDate)
        : undefined,
    };

    const reportParams = report.reportParameters;
    if (reportParams) {
      properties.dayCount = this.dateRangeService.getNumberOfDays(
        new Date(reportParams.startDate),
        new Date(reportParams.endDate)
      );
    }

    this.tracker.trackEventData({
      action: 'Report Downloaded',
      category: report.reportName,
      properties,
    });
  }

  public getReportDownloadUrl(report: DownloadableReport) {
    let url = `${REPORTING_API}?referenceId=${report.reportReference}`;

    // if it's a pathway report, we need to add the pathwayId
    if (!!this.pathwayId) {
      url += `&pathwayId=${this.pathwayId}`;
    }
    return url;
  }

  public generateReport() {
    // For the reports generated directly here, rather than from the Create Report Modal, use the
    // UTC 'yesterday' date as the `EndDate` parameter, to more closely align with the logic used
    // by the date range picker in the Create Report Modal.
    const yesterdayUTCFormat = this.dateRangeService.getDateFormat(
      this.dateRangeService.getDaysAgoUTC(1)
    );

    let action: Observable<unknown>;
    if (this.isSkillInventoryClient) {
      const reportName = 'Skill Report';
      this.isLoading = true;
      const skillReport = this.availableReports.find(
        (report) => report.reportType === reportName
      );
      action = this.downloadableReportsApiService
        .generateDownloadableReport(reportName, {
          organizationId: this.orgId,
          reportId: skillReport.reportId,
          reportName: reportName,
          endDate: yesterdayUTCFormat,
          location: 'Org Insights',
        })
        .pipe(
          tap(() => {
            this.notifier.showSuccess(
              this.translate.instant('dgDownloadableReports_ToastMessage')
            );
          })
        );
    } else if (this.isSkillAnalyticsClient) {
      const reportName = 'Skill Analytics Top 1000 Skills Rated';
      this.isLoading = true;
      const skillReport = this.availableReports.find(
        (report) => report.reportType === reportName
      );
      action = this.downloadableReportsApiService
        .generateDownloadableReport(reportName, {
          organizationId: this.orgId,
          reportId: skillReport.reportId,
          reportName: reportName,
          endDate: yesterdayUTCFormat,
          location: 'Org Insights',
        })
        .pipe(
          tap(() => {
            this.notifier.showSuccess(
              this.translate.instant('dgDownloadableReports_ToastMessage')
            );
          })
        );
    } else if (this.pathwayId) {
      this.isLoading = true;
      action = this.downloadableReportsApiService
        .generateDownloadableReport(this.pathwayReport.reportType, {
          organizationId: this.orgId,
          pathwayId: this.pathwayId,
          selection: this.pathwayId,
          reportName: this.pathwayReport.reportType,
          reportId: this.pathwayReport.reportId,
          endDate: yesterdayUTCFormat,
          location: 'Pathway Insights',
        })
        .pipe(
          tap(() => {
            this.notifier.showSuccess(
              this.translate.instant('dgDownloadableReports_ToastMessage')
            );
          })
        );
    } else {
      action = this.downloadableReportsModalsService.openCreateReportModal({
        availableReports: this.availableReports,
        canViewReports: this.canViewReports,
        defaultGroupId: this.defaultGroupId,
        defaultGroupIdList:
          !this.defaultGroupIdList && this.defaultGroupId
            ? this.defaultGroupId.toString()
            : this.defaultGroupIdList,
        groups: this.groups,
        initDaysPrior: this.initDaysPrior,
        maxDays: this.maxDays,
        orgId: this.orgId,
        defaultReport: this.defaultReport,
      });
    }

    return this.ngZone.run(() => {
      action
        .pipe(
          tap(() => {
            this.getReports();
          }),
          // catch and ignore errors from modal close
          catchError(() => EMPTY)
        )
        .subscribe();
    });
  }

  /**
   * Bind the Angular route snapshot data and queryParams
   */
  public getRouteSnapshot() {
    const params =
      Object.keys(this.route.snapshot?.queryParams).length > 0
        ? this.route.snapshot.queryParams
        : undefined;

    if (!params) {
      return;
    }
    this.defaultGroupId = params.gId;

    // extract and parse params, if present
    if (params.groupIdList) {
      this.defaultGroupIdList = decodeURIComponent(params.groupIdList);
    }
    if (params.defaultReport) {
      this.defaultReport = decodeURIComponent(params.defaultReport);
    }
    if (params.location) {
      this.location = decodeURIComponent(params.location);
    }
    this.source = 'org';
  }

  private initializeColumns(): void {
    const isPathwayReport = !!this.pathwayId;
    const isSkillClient =
      this.isSkillInventoryClient || this.isSkillAnalyticsClient;

    this.columns = [
      {
        label: this.i18n.dgDownloadableReports_ReportType,
        headClasses: 'l_w30',
        prop: 'reportName',
      },
      {
        hide: isPathwayReport || isSkillClient,
        label: this.i18n.Core_Group,
        headClasses: 'l_w30',
        template: this.groupNameColumnTemplate,
      },
      {
        hide: isPathwayReport || isSkillClient,
        label: this.i18n.dgDownloadableReports_DateRange,
        headClasses: 'l_w25',
        template: this.dateRangeColumnTemplate,
      },
      {
        hide: !isPathwayReport && !isSkillClient,
        label: this.i18n.dgDownloadableReports_DateCreated,
        template: this.dateCreatedColumnTemplate,
        headClasses: 'l_w55',
      } as DataColumnDate,
      {
        // NOTE: Status column does not have a header
        label: '',
        headClasses: 'l_w15',
        bodyClasses: 'right-text',
        template: this.reportStatusColumnTemplate,
      },
    ];
  }
}
